import { Link } from 'react-router';
import { buildMeta, localBusinessSchema, siteIdentitySchema, faqSchema } from '~/lib/seo';
import { SITE, GUARANTEE, activeTrustPoints } from '~/data/site';
import { coreServices, onRequestServices } from '~/data/services';
import { shippedIndustries } from '~/data/industries';
import { VISIT_MINIMUM, RATE_CARD } from '@shared/pricing';
import { ProofOfVisit } from '~/components/brand/proof-of-visit';
import { Mark15 } from '~/components/brand/mark-15';

/**
 * The five questions a facility manager actually asks first, answered on the page and
 * mirrored into FAQPage schema.
 *
 * Both the rendered markup and the JSON-LD read from this one array. Structured data
 * that asserts an answer the page does not display is a guidelines violation, and the
 * only reliable way to avoid it is to make the page and the schema the same source.
 */
export const HOME_FAQ = [
  {
    q: 'How much does commercial cleaning cost in Tampa Bay?',
    a: `Office cleaning runs roughly $0.08–$0.14 per square foot per visit depending on facility type and how often we come, with a $${VISIT_MINIMUM} minimum per visit. Every rate is published on the pricing page rather than quoted only after a phone call. Florida charges sales tax on commercial cleaning — 7.5% in Hillsborough, 7% in Pinellas, Pasco and Polk — and it appears as its own line on the invoice.`,
  },
  {
    q: 'Do you require a long-term contract?',
    a: 'No. Agreements are month to month with 30 days notice on either side and no early-termination fee. If the work is good you keep us because you want to, not because the paperwork traps you.',
  },
  {
    q: 'When do you clean?',
    a: 'After hours — evenings, nights and weekends. That is the whole idea behind the name. Your staff go home, we come in, and the building is ready before anyone arrives the next morning.',
  },
  {
    q: 'Which areas do you serve?',
    a: `We are based in Brandon and work across Tampa Bay — ${SITE.serviceCities.join(', ')} and the surrounding parts of Hillsborough, Pinellas, Pasco and Polk counties.`,
  },
  {
    q: 'Is it the same crew every visit?',
    a: 'Yes. The same people clean your building each visit and a named supervisor owns the account, so you are not re-explaining your floor plan to a stranger every week. Every visit ends with a signed checklist.',
  },
] as const;

export function meta() {
  return buildMeta({
    title: 'Commercial cleaning in Tampa Bay',
    description:
      'Nightly janitorial for Tampa Bay offices, clinics and churches. Same crew every visit, a written checklist, no annual contract, and prices published.',
    path: '/',
    jsonLd: [localBusinessSchema(), siteIdentitySchema(), faqSchema(HOME_FAQ)],
  });
}

export default function Home() {
  const trust = activeTrustPoints();

  return (
    <>
      {/* ── Above the fold ─────────────────────────────────────────────── */}
      <section className="hero dusk grain">
        <div className="shell">
          <div className="hero-grid">
          <div className="hero-inner">
            <p className="eyebrow-pill">Commercial cleaning · Tampa Bay</p>

            {/* Outcome, not category. Every competitor uses "Commercial Cleaning
                Services in Tampa, FL" — that headline differentiates nothing. */}
            <h1>We clean it after you go home.</h1>

            <p className="lede">
              Nightly janitorial for Tampa Bay offices, clinics and churches — the same crew every
              visit, a written checklist, and no annual contract.
            </p>

            <div className="btn-row">
              <Link to="/quote" className="btn btn-primary">
                Get a quote
              </Link>
            </div>

            {/* Trust line renders only the claims that are true today. */}
            {trust.length > 0 && (
              <ul className="trust-line">
                {trust.map((t) => (
                  <li key={t.gate}>{t.text}</li>
                ))}
              </ul>
            )}
          </div>

            {/* The peak moment. Not a blank template — the completed record the
                client receives, so the proof argument is shown, not described. */}
            <ProofOfVisit />
          </div>
        </div>
      </section>

      {/* Reeded glass: the blind-slat motif as the material it comes from.
          A seam between the dusk hero and the night band, not decoration. */}
      <div className="reeded-seam" aria-hidden="true" />

      {/* ── The night band: what a visit actually is ────────────────────── */}
      <section className="night-band">
        <div className="shell">
          <div className="stack-lg">
            <div className="stack-sm">
              <p className="eyebrow">A visit, start to finish</p>
              <h2>Second shift is a real thing.<br />Not a slogan.</h2>
              <p style={{ maxWidth: '46ch' }}>
                It is the crew that comes in when your people go home. Here is what that looks like
                in a typical office — and what you get in the morning.
              </p>
            </div>

            <ol className="timeline">
              <li>
                <span className="t-time">6:15 pm</span>
                <span className="t-what">
                  Crew arrives and clocks in on site
                  <span className="t-note">
                    Same two people you met at the walkthrough. Not a rotating pool.
                  </span>
                </span>
              </li>
              <li>
                <span className="t-time">6:20 pm</span>
                <span className="t-what">
                  Restrooms first, not last
                  <span className="t-note">
                    Restrooms and empty dispensers are the top two facility-manager complaints, at
                    26% and 25%. They get done while the crew is fresh.
                  </span>
                </span>
              </li>
              <li>
                <span className="t-time">7:00 pm</span>
                <span className="t-what">
                  Trash, touchpoints, kitchen, floors
                  <span className="t-note">
                    Door handles, switches, rails, shared desks. The checklist is the contract.
                  </span>
                </span>
              </li>
              <li>
                <span className="t-time">8:30 pm</span>
                <span className="t-what">
                  Walk-through against the checklist
                  <span className="t-note">Anything not signed off does not count as done.</span>
                </span>
              </li>
              <li>
                <span className="t-time">8:45 pm</span>
                <span className="t-what">
                  Lights off, doors locked, alarm set — signed
                  <span className="t-note">
                    By name, every visit. Because &ldquo;the office was left unlocked over the
                    weekend&rdquo; is a real thing that happens to people.
                  </span>
                </span>
              </li>
            </ol>
          </div>
        </div>
      </section>

      {/* ── Pain → promise, in the buyer's own words ────────────────────── */}
      <section className="section">
        <div className="shell">
          <div className="section-head">
            <span className="rule-tick" />
            <p className="eyebrow">Why people leave their cleaner</p>
            <h2>Nobody fires a cleaner<br />over dirt.</h2>
            <p>
              They leave over silence and lock-in. These are the complaints that come up most often
              — in their words — and what we do about each one.
            </p>
          </div>

          <ul className="pairs">
            {PAIN_PROMISE.map((row) => (
              <li key={row.pain}>
                <span className="pair-pain">&ldquo;{row.pain}&rdquo;</span>
                <span className="pair-promise">{row.promise}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Pricing teaser — the Tampa-unique differentiator ────────────── */}
      <section className="section section-ground">
        <div className="shell">
          <div className="split">
            <div className="stack">
              <p className="eyebrow">Pricing</p>
              <h2>We publish our prices.<br />Nobody else here does.</h2>
              <p>
                We checked seventeen commercial cleaning companies across Tampa Bay. Not one of them
                puts a price anywhere on their site. That is not an accident — it is a negotiating
                position, and it costs you a week of phone tag before you learn whether they are
                even in your range.
              </p>
              <p>
                Ours are on the page. Per square foot, per visit, by facility type and how often we
                come. Minimum {formatMoney(VISIT_MINIMUM)} a visit.
              </p>
              <div className="btn-row">
                <Link to="/pricing" className="btn btn-primary">
                  See the rates
                </Link>
              </div>
            </div>

            <div className="split-sticky">
              <div className="table-wrap">
                <table className="data">
                  <caption className="small" style={{ captionSide: 'bottom', padding: '0.75rem 1rem', textAlign: 'left' }}>
                    Per square foot, per visit. Full table on the pricing page.
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Facility</th>
                      <th scope="col" className="numeric">
                        Weekly
                      </th>
                      <th scope="col" className="numeric">
                        3×/wk
                      </th>
                      <th scope="col" className="numeric">
                        Nightly
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(['office', 'retail', 'medical', 'industrial'] as const).map((key) => {
                      const rates = RATE_CARD[key]!;
                      return (
                        <tr key={key}>
                          <th scope="row">{FACILITY_SHORT[key]}</th>
                          <td className="numeric">${rates.weekly.toFixed(2)}</td>
                          <td className="numeric">${rates['3x'].toFixed(2)}</td>
                          <td className="numeric">${rates['5x'].toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Services ───────────────────────────────────────────────────── */}
      <section className="section">
        <div className="shell">
          <div className="section-head">
            <span className="rule-tick" />
            <p className="eyebrow">What we do</p>
            <h2>Two things we do ourselves.<br />Three we will quote.</h2>
            <p>
              We would rather name the line than blur it. Everything below it we will still price
              for you — and if a specialist would do it better, we say so.
            </p>
          </div>

          <ul className="index-list">
            {coreServices().map((s, i) => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`} className="index-row">
                  <span className="index-num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="index-body">
                    <span className="index-name">{s.name}</span>
                    <span className="index-note">{s.blurb}</span>
                    <span className="index-quote">&ldquo;{s.worry}&rdquo;</span>
                  </span>
                </Link>
              </li>
            ))}
            {onRequestServices().map((s, i) => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`} className="index-row">
                  <span className="index-num">{String(i + 3).padStart(2, '0')}</span>
                  <span className="index-body">
                    <span className="index-tag">On request</span>
                    <span className="index-name">{s.name}</span>
                    <span className="index-note">{s.blurb}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Industries ─────────────────────────────────────────────────── */}
      <section className="section section-ground">
        <div className="shell">
          <div className="section-head">
            <span className="rule-tick" />
            <p className="eyebrow">Who we clean for</p>
            <h2>A dental practice and a church<br />are not the same job.</h2>
            <p>
              They are afraid of different things, so they get different answers. Pick the one that
              sounds like your building.
            </p>
          </div>

          <ul className="index-list">
            {shippedIndustries().map((i, n) => (
              <li key={i.slug}>
                <Link to={`/industries/${i.slug}`} className="index-row">
                  <span className="index-num">{String(n + 1).padStart(2, '0')}</span>
                  <span className="index-body">
                    <span className="index-name">{i.name}</span>
                    <span className="index-quote">&ldquo;{i.fear}&rdquo;</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── The guarantee, defined ─────────────────────────────────────── */}
      {SITE.claims.guaranteeCommitted && (
        <section className="section-tight">
          <div className="shell-narrow">
            <div className="guarantee">
              <p className="eyebrow">Our guarantee</p>
              <h3>{GUARANTEE.headline}</h3>
              <p>{GUARANTEE.body}</p>
            </div>
          </div>
        </section>
      )}

      {/* ── Questions ──────────────────────────────────────────────────── */}
      {/* Rendered from HOME_FAQ, the same array that feeds the FAQPage JSON-LD in
          meta(). One source, so the structured data can never claim an answer the
          page does not actually show. */}
      <section className="section-tight">
        <div className="shell">
          <div className="faq">
            <h2>Questions we get asked first</h2>
            {HOME_FAQ.map((item) => (
              <details key={item.q}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
            <p className="muted" style={{ marginTop: '1.25rem' }}>
              More on cost, including the full rate card and what is always included,{' '}
              <Link to="/pricing">on the pricing page</Link>. For where we work, see the{' '}
              <Link to="/service-area">service area</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* ── Close ──────────────────────────────────────────────────────── */}
      <section className="dawn">
        <div className="shell">
          <div className="dawn-grid">
          <div className="cta-inner">
            <h2>Twenty minutes in your building.<br />Then a real number.</h2>
            <p>
              We walk the space, count what is actually there, and send a fixed price. No estimate
              that changes after you sign.
            </p>
            <div className="btn-row">
              <Link to="/quote" className="btn btn-primary">
                Book a walkthrough
              </Link>
            </div>

          </div>

            {/* Dusk → dark → dawn closes here. The same horizon that opens the
                shift is the morning the client actually walks into. It is the
                only illustration on the page, and it is the payoff, not a badge.

                This is THE MARK at illustration scale, not a lookalike device. It
                used to be `ShiftHorizon` — a separate drawing of the same picture
                with its own proportions, four building masses instead of three and
                a different arc. Two versions of one image on one page is how a logo
                stops being a logo. Since the board made this composition the primary
                mark, the page renders the mark itself and animates it. */}
            <div className="dawn-horizon">
              <Mark15 animate height={420} title="Dusk over a commercial building with one office light on" />
              <p className="dawn-caption">7 am. Nobody in the frame.</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

/**
 * Verbatim complaints from the buyer research, paired with what we do.
 *
 * The left column is quoted from real reviews — that is the point. It is the buyer's
 * own language, not a marketing paraphrase of it.
 */
const PAIN_PROMISE = [
  {
    pain: 'I could never even tell if they showed up.',
    promise: 'Clocked in on site, checklist closed out, sent to you.',
  },
  {
    pain: 'The only task performed consistently is emptying the garbage.',
    promise: 'Restrooms and dispensers checked every single visit.',
  },
  {
    pain: 'I had four account reps in the time I was with them.',
    promise: 'One named person owns your building.',
  },
  {
    pain: 'They constantly change teams.',
    promise: 'The same crew, every time. You know their names.',
  },
  {
    pain: 'The offices were left unlocked over the weekend.',
    promise: 'Lock-up and alarm signed off by name, every visit.',
  },
  {
    pain: 'They use contracts to keep you locked in.',
    promise: 'Month to month. 30 days out. No termination fee.',
  },
  {
    pain: 'They blame you and tell you they need to charge more.',
    promise: 'Missed something? We fix it in 24 hours, free.',
  },
  {
    pain: 'I was told it would take three people. Only two showed up.',
    promise: 'Crew size is written into the quote. Three means three.',
  },
];

const FACILITY_SHORT = {
  office: 'Office',
  retail: 'Retail',
  medical: 'Medical',
  industrial: 'Industrial',
} as const;

function formatMoney(n: number) {
  return `$${n}`;
}
